import React, { useState, useEffect, useRef } from 'react';
import './ComparativeAnalysis.css';
import { API_BASE_URL } from './constants';
import { authService } from './services/authService';

function ComparativeAnalysis() {
  const [selectedTestIds, setSelectedTestIds] = useState([]);
  const [reportName, setReportName] = useState('');
  const [selectedParameters, setSelectedParameters] = useState({
    spo2: true,
    bpm: true,
    temperature: true,
    hyperemiaIndex: true,
    volumeIndex: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Filter and sort states
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Available tests for test selection table
  const [availableTests, setAvailableTests] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);

  // Test selection table states
  const [testTableFilters, setTestTableFilters] = useState({});
  const [testTableSort, setTestTableSort] = useState({ key: 'timeStamp', direction: 'desc' });
  const [testTablePage, setTestTablePage] = useState(1);
  const [testTableItemsPerPage, setTestTableItemsPerPage] = useState(10);

  // Clone mode tracking
  const [isCloneMode, setIsCloneMode] = useState(false);

  // Tag-based report creation
  const [selectedTag, setSelectedTag] = useState('');
  const [tagReportName, setTagReportName] = useState('');
  const [isTagSubmitting, setIsTagSubmitting] = useState(false);
  const [tagStatusMessage, setTagStatusMessage] = useState('');

  // CSV upload state
  const [csvUploadMessage, setCsvUploadMessage] = useState('');

  // Schedule state: { [reportId]: { input: "HH:MM", saving: bool } }
  const [scheduleMap, setScheduleMap] = useState({});

  // Rerun modal state
  const [rerunModalReportIds, setRerunModalReportIds] = useState(null);
  const [cleanRerunFlag, setCleanRerunFlag] = useState('n');

  // Bulk selection of comparative reports for batch rerun
  const [selectedReportIds, setSelectedReportIds] = useState([]);

  // Polling interval ref for report status updates
  const pollingIntervalRef = useRef(null);

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  const startPolling = () => {
    stopPolling();
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis`, {
          headers: authService.getAuthHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          setReports(data);
          const hasPending = data.some(r => r.status === 'Pending' || r.status === 'Processing');
          if (!hasPending) {
            stopPolling();
          }
        }
      } catch (error) {
        console.error('Error polling reports:', error);
      }
    }, 15000);
  };

  // Load selected test IDs from localStorage on mount
  useEffect(() => {
    const storedIds = localStorage.getItem('selectedTestIds');
    if (storedIds) {
      try {
        const ids = JSON.parse(storedIds);
        setSelectedTestIds(ids);
      } catch (error) {
        console.error('Error parsing stored test IDs:', error);
        setStatusMessage('Error loading selected tests');
      }
    }
  }, []);

  // Fetch comparative reports on mount and after submission
  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data);
      } else {
        console.error('Failed to fetch reports');
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  useEffect(() => {
    fetchReports();
    fetchAvailableTests();
    fetchTags();
  }, []);

  // Clean up polling interval on unmount
  useEffect(() => () => stopPolling(), []);

  // Fetch all available tags
  const fetchTags = async () => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/tags`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTags(data);
      } else {
        console.error('Failed to fetch tags');
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  // Fetch all available tests for the test selection table
  const fetchAvailableTests = async () => {
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/entries`, {
        headers: authService.getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableTests(data);
      } else {
        console.error('Failed to fetch available tests');
      }
    } catch (error) {
      console.error('Error fetching available tests:', error);
    }
  };

  const handleAddTestId = (testId) => {
    if (testId && !selectedTestIds.includes(testId)) {
      const newTestIds = [...selectedTestIds, testId];
      setSelectedTestIds(newTestIds);
      localStorage.setItem('selectedTestIds', JSON.stringify(newTestIds));
    }
  };

  const handleRemoveTestId = (testId) => {
    const newTestIds = selectedTestIds.filter(id => id !== testId);
    setSelectedTestIds(newTestIds);
    localStorage.setItem('selectedTestIds', JSON.stringify(newTestIds));
  };

  const handleParameterToggle = (parameter) => {
    setSelectedParameters(prev => ({
      ...prev,
      [parameter]: !prev[parameter]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (selectedTestIds.length === 0) {
      setStatusMessage('No tests selected. Please go back to Test Results and select tests.');
      return;
    }

    if (!reportName.trim()) {
      setStatusMessage('Please enter a report name');
      return;
    }

    const selectedParams = Object.keys(selectedParameters).filter(
      param => selectedParameters[param]
    );

    if (selectedParams.length === 0) {
      setStatusMessage('Please select at least one parameter for analysis');
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('Starting comparative analysis...');

    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis`, {
        method: 'POST',
        headers: {
          ...authService.getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          testIds: selectedTestIds,
          reportName: reportName.trim(),
          parameters: selectedParams,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setStatusMessage(`Success! ${result.message}`);
        // Clear selection from localStorage
        localStorage.removeItem('selectedTestIds');
        // Reset form
        setReportName('');
        setSelectedTestIds([]);
        setIsCloneMode(false);
        // Refresh reports list and start polling until completed
        fetchReports();
        startPolling();
      } else {
        setStatusMessage(`Error: ${result.error || 'Failed to run comparative analysis'}`);
      }
    } catch (error) {
      console.error('Error running comparative analysis:', error);
      setStatusMessage(`Error: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const testsForSelectedTag = selectedTag
    ? availableTests.filter(t => t.tags && t.tags.some(id => id.toString() === selectedTag))
    : [];

  const handleTagSubmit = async (e) => {
    e.preventDefault();

    if (!selectedTag) {
      setTagStatusMessage('Please select a tag');
      return;
    }

    if (testsForSelectedTag.length < 2) {
      setTagStatusMessage('Selected tag must have at least 2 tests');
      return;
    }

    if (!tagReportName.trim()) {
      setTagStatusMessage('Please enter a report name');
      return;
    }

    const selectedParams = Object.keys(selectedParameters).filter(param => selectedParameters[param]);
    if (selectedParams.length === 0) {
      setTagStatusMessage('Please select at least one parameter for analysis');
      return;
    }

    setIsTagSubmitting(true);
    setTagStatusMessage('Starting comparative analysis...');

    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis`, {
        method: 'POST',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ tagId: selectedTag, reportName: tagReportName.trim(), parameters: selectedParams }),
      });
      const result = await response.json();
      if (response.ok) {
        setTagStatusMessage(`Success! ${result.message}`);
        setTagReportName('');
        setSelectedTag('');
        fetchReports();
        startPolling();
      } else {
        setTagStatusMessage(`Error: ${result.error || 'Failed to run comparative analysis'}`);
      }
    } catch (error) {
      console.error('Error running tag-based comparative analysis:', error);
      setTagStatusMessage(`Error: ${error.message}`);
    } finally {
      setIsTagSubmitting(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatLastRerun = (report) => {
    if (!report.lastRerunAt) return '—';
    return (
      <>
        {new Date(report.lastRerunAt).toLocaleString()}
        <br />
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          by {report.lastRerunBy || 'Unknown'} ({report.lastRerunType || 'Unknown'})
        </span>
      </>
    );
  };

  // Handle sort
  const handleSort = (field) => {
    let direction = 'asc';
    if (sortConfig.key === field && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: field, direction });
  };

  // Filter and sort reports
  const getFilteredAndSortedReports = () => {
    let filteredReports = [...reports];

    // Apply column filters
    Object.keys(columnFilters).forEach((field) => {
      const filterValue = columnFilters[field];
      if (filterValue && filterValue.trim()) {
        filteredReports = filteredReports.filter((report) => {
          const value = field === 'testsCount'
            ? String(report.testIds.length)
            : field === 'parameters'
            ? report.parameters.join(', ')
            : String(report[field] || '');
          return value.toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (sortConfig.key) {
      filteredReports.sort((a, b) => {
        let aValue, bValue;

        if (sortConfig.key === 'testsCount') {
          aValue = a.testIds.length;
          bValue = b.testIds.length;
        } else if (sortConfig.key === 'parameters') {
          aValue = a.parameters.join(', ');
          bValue = b.parameters.join(', ');
        } else {
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
        }

        if (typeof aValue === 'string') {
          return sortConfig.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }

    return filteredReports;
  };

  // Pagination
  const filteredReports = getFilteredAndSortedReports();
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1);
  };

  // Handle test selection from table
  const handleTestCheckboxToggle = (testId) => {
    if (selectedTestIds.includes(testId)) {
      handleRemoveTestId(testId);
    } else {
      handleAddTestId(testId);
    }
  };

  // Handle test table sort
  const handleTestTableSort = (field) => {
    let direction = 'asc';
    if (testTableSort.key === field && testTableSort.direction === 'asc') {
      direction = 'desc';
    }
    setTestTableSort({ key: field, direction });
  };

  // Get duration display from test metadata (matches TestResults page format)
  const calculateDuration = (test) => {
    if (test.durations) {
      // Return as-is to match TestResults page format: [60,300,60]
      return test.durations;
    }
    return 'N/A';
  };

  // Filter and sort test table
  const getFilteredAndSortedTests = () => {
    let filteredTests = [...availableTests];

    // Apply column filters
    Object.keys(testTableFilters).forEach((field) => {
      const filterValue = testTableFilters[field];
      if (filterValue && filterValue.trim()) {
        filteredTests = filteredTests.filter((test) => {
          let value = '';
          if (field === 'testInstance') {
            value = test.testInstance || '';
          } else if (field === 'timestamp') {
            value = new Date(test.timeStamp * 1000).toLocaleString();
          } else if (field === 'duration') {
            value = String(calculateDuration(test));
          } else if (field === 'tags') {
            const tagIds = test.tags || [];
            const tagNames = tagIds.map(tagId => {
              const tag = availableTags.find(t => t._id === tagId);
              return tag ? tag.name : '';
            }).filter(Boolean);
            value = tagNames.join(', ');
          } else if (field === 'comment') {
            value = test.comment || '';
          }
          return value.toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });

    // Apply sorting
    if (testTableSort.key) {
      filteredTests.sort((a, b) => {
        let aValue, bValue;

        if (testTableSort.key === 'testInstance') {
          aValue = a.testInstance || '';
          bValue = b.testInstance || '';
        } else if (testTableSort.key === 'timeStamp') {
          aValue = a.timeStamp || 0;
          bValue = b.timeStamp || 0;
        } else if (testTableSort.key === 'tags') {
          const aTagIds = a.tags || [];
          const aTagNames = aTagIds.map(tagId => {
            const tag = availableTags.find(t => t._id === tagId);
            return tag ? tag.name : '';
          }).filter(Boolean);
          aValue = aTagNames.join(', ');

          const bTagIds = b.tags || [];
          const bTagNames = bTagIds.map(tagId => {
            const tag = availableTags.find(t => t._id === tagId);
            return tag ? tag.name : '';
          }).filter(Boolean);
          bValue = bTagNames.join(', ');
        } else if (testTableSort.key === 'comment') {
          aValue = a.comment || '';
          bValue = b.comment || '';
        } else if (testTableSort.key === 'duration') {
          const aDuration = calculateDuration(a);
          const bDuration = calculateDuration(b);
          // Calculate total duration for sorting by summing array values
          if (aDuration === 'N/A') {
            aValue = -1;
          } else {
            try {
              const aDurArray = JSON.parse(aDuration);
              aValue = Array.isArray(aDurArray) ? aDurArray.reduce((sum, dur) => sum + dur, 0) : -1;
            } catch (e) {
              aValue = -1;
            }
          }
          if (bDuration === 'N/A') {
            bValue = -1;
          } else {
            try {
              const bDurArray = JSON.parse(bDuration);
              bValue = Array.isArray(bDurArray) ? bDurArray.reduce((sum, dur) => sum + dur, 0) : -1;
            } catch (e) {
              bValue = -1;
            }
          }
        }

        if (typeof aValue === 'string') {
          return testTableSort.direction === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else {
          return testTableSort.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        }
      });
    }

    return filteredTests;
  };

  // Test table pagination
  const filteredTests = getFilteredAndSortedTests();
  const testTableTotalPages = Math.ceil(filteredTests.length / testTableItemsPerPage);
  const paginatedTests = filteredTests.slice(
    (testTablePage - 1) * testTableItemsPerPage,
    testTablePage * testTableItemsPerPage
  );

  const handleTestTablePageChange = (page) => {
    setTestTablePage(page);
  };

  const handleTestTableItemsPerPageChange = (value) => {
    setTestTableItemsPerPage(value);
    setTestTablePage(1);
  };

  const handleCloneReport = (report) => {
    // Populate the form with the cloned report's data
    setSelectedTestIds([...report.testIds]);
    setReportName(`${report.reportName}_copy`);
    setSelectedParameters(
      report.parameters.reduce((acc, param) => {
        acc[param] = true;
        return acc;
      }, {
        spo2: false,
        bpm: false,
        temperature: false,
        hyperemiaIndex: false,
        volumeIndex: false,
      })
    );

    // Save to localStorage so it persists
    localStorage.setItem('selectedTestIds', JSON.stringify(report.testIds));

    // Set clone mode
    setIsCloneMode(true);

    // Scroll to top to show the form
    window.scrollTo({ top: 0, behavior: 'smooth' });

    setStatusMessage(`Cloned report "${report.reportName}". You can now modify test IDs, parameters, and report name before submitting.`);
  };

  const handleCancelClone = () => {
    // Clear all cloned data
    setSelectedTestIds([]);
    setReportName('');
    setSelectedParameters({
      spo2: true,
      bpm: true,
      temperature: true,
      hyperemiaIndex: true,
      volumeIndex: true,
    });
    localStorage.removeItem('selectedTestIds');
    setIsCloneMode(false);
    setCsvUploadMessage('');
    setStatusMessage('Clone cancelled. Form has been reset.');
  };

  const handleCsvUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.csv')) {
      setCsvUploadMessage('Error: Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        // Parse CSV - expecting one test ID per line or comma-separated
        const lines = text.split(/[\r\n]+/).filter(line => line.trim());
        const testIds = [];

        lines.forEach(line => {
          // Split by comma and trim each value
          const ids = line.split(',').map(id => id.trim()).filter(id => id);
          testIds.push(...ids);
        });

        if (testIds.length === 0) {
          setCsvUploadMessage('Error: No test IDs found in CSV file');
          return;
        }

        // Validate test IDs exist in availableTests
        const validTestIds = testIds.filter(id =>
          availableTests.some(test => test.testInstance === id)
        );
        const invalidTestIds = testIds.filter(id =>
          !availableTests.some(test => test.testInstance === id)
        );

        // Combine with existing selected test IDs (remove duplicates)
        const combinedTestIds = [...new Set([...selectedTestIds, ...validTestIds])];
        setSelectedTestIds(combinedTestIds);
        localStorage.setItem('selectedTestIds', JSON.stringify(combinedTestIds));

        // Show feedback message
        let message = `✓ Successfully added ${validTestIds.length} test ID(s) from CSV`;
        if (invalidTestIds.length > 0) {
          message += `. Warning: ${invalidTestIds.length} test ID(s) not found and were skipped.`;
        }
        setCsvUploadMessage(message);
        setStatusMessage(message);

        // Clear the file input
        event.target.value = '';
      } catch (error) {
        setCsvUploadMessage('Error: Failed to parse CSV file');
        console.error('CSV parsing error:', error);
      }
    };

    reader.onerror = () => {
      setCsvUploadMessage('Error: Failed to read file');
    };

    reader.readAsText(file);
  };

  const handleScheduleChange = (reportId, value) => {
    setScheduleMap(prev => ({ ...prev, [reportId]: { ...prev[reportId], input: value } }));
  };

  const handleScheduleSave = async (reportId) => {
    const entry = scheduleMap[reportId] || {};
    const scheduleTime = entry.input ?? '';
    setScheduleMap(prev => ({ ...prev, [reportId]: { ...prev[reportId], saving: true } }));
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis/${reportId}/schedule`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduleTime: scheduleTime || null }),
      });
      if (response.ok) {
        // Reflect saved state back into the reports list
        setReports(prev => prev.map(r =>
          r.reportId === reportId ? { ...r, scheduleTime: scheduleTime || null } : r
        ));
      } else {
        const result = await response.json();
        alert(`Error: ${result.error || 'Failed to save schedule'}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setScheduleMap(prev => ({ ...prev, [reportId]: { ...prev[reportId], saving: false } }));
  };

  const handleScheduleClear = async (reportId) => {
    setScheduleMap(prev => ({ ...prev, [reportId]: { input: '', saving: true } }));
    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis/${reportId}/schedule`, {
        method: 'PUT',
        headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduleTime: null }),
      });
      if (response.ok) {
        setReports(prev => prev.map(r =>
          r.reportId === reportId ? { ...r, scheduleTime: null } : r
        ));
      } else {
        const result = await response.json();
        alert(`Error: ${result.error || 'Failed to clear schedule'}`);
      }
    } catch (error) {
      alert(`Error: ${error.message}`);
    }
    setScheduleMap(prev => ({ ...prev, [reportId]: { ...prev[reportId], saving: false } }));
  };

  const handleRerunReport = (reportId) => {
    setCleanRerunFlag('n');
    setRerunModalReportIds([reportId]);
  };

  const handleBulkRerun = () => {
    if (selectedReportIds.length === 0) return;
    setCleanRerunFlag('n');
    setRerunModalReportIds(selectedReportIds);
  };

  const handleReportCheckboxToggle = (reportId) => {
    setSelectedReportIds(prev =>
      prev.includes(reportId) ? prev.filter(id => id !== reportId) : [...prev, reportId]
    );
  };

  const handleSelectAllReports = () => {
    if (selectedReportIds.length === paginatedReports.length) {
      setSelectedReportIds([]);
    } else {
      setSelectedReportIds(paginatedReports.map(r => r.reportId));
    }
  };

  const handleConfirmRerun = async () => {
    const reportIds = rerunModalReportIds;
    setRerunModalReportIds(null);
    try {
      const results = await Promise.all(reportIds.map(reportId =>
        authService.apiFetch(`${API_BASE_URL}/comparative-analysis/rerun/${reportId}`, {
          method: 'POST',
          headers: { ...authService.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ cleanRerunFlag }),
        }).then(res => res.json().then(result => ({ reportId, ok: res.ok, result })))
      ));
      const failed = results.filter(r => !r.ok);
      if (failed.length > 0) {
        alert(`Failed to rerun: ${failed.map(f => `${f.reportId} (${f.result.error || 'unknown error'})`).join(', ')}`);
      }
      setSelectedReportIds([]);
      fetchReports();
      startPolling();
    } catch (error) {
      console.error('Error rerunning analysis:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm(`Are you sure you want to delete report "${reportId}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await authService.apiFetch(`${API_BASE_URL}/comparative-analysis/${reportId}`, {
        method: 'DELETE',
        headers: authService.getAuthHeaders(),
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Report deleted successfully: ${result.message}`);
        // Refresh reports list
        fetchReports();
      } else {
        alert(`Error: ${result.error || 'Failed to delete report'}`);
      }
    } catch (error) {
      console.error('Error deleting report:', error);
      alert(`Error: ${error.message}`);
    }
  };

  return (
    <div className="comparative-analysis-container">
      <h2>Comparative Analysis</h2>

      {/* Tag-based Report Creation */}
      <div className="form-section" style={{ marginBottom: '30px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <h3>Create Report by Tag</h3>
        <form onSubmit={handleTagSubmit}>
          <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Select Tag</label>
              <select value={selectedTag} onChange={(e) => setSelectedTag(e.target.value)} disabled={isTagSubmitting}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px' }}>
                <option value="">-- Choose a tag --</option>
                {availableTags.map(tag => <option key={tag._id} value={tag._id}>{tag.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>Report Name</label>
              <input type="text" value={tagReportName} onChange={(e) => setTagReportName(e.target.value)}
                placeholder="Enter report name" disabled={isTagSubmitting}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
          </div>
          {selectedTag && (
            <div style={{ marginBottom: '15px', padding: '8px 12px', borderRadius: '4px', fontSize: '14px',
              backgroundColor: testsForSelectedTag.length >= 2 ? '#d4edda' : '#fff3cd',
              color: testsForSelectedTag.length >= 2 ? '#155724' : '#856404',
              border: `1px solid ${testsForSelectedTag.length >= 2 ? '#c3e6cb' : '#ffeeba'}` }}>
              {testsForSelectedTag.length} test{testsForSelectedTag.length !== 1 ? 's' : ''} found with this tag
              {testsForSelectedTag.length < 2 ? ' (need at least 2)' : ''}
            </div>
          )}
          <div className="parameters-grid" style={{ marginBottom: '15px' }}>
            {Object.entries({ spo2: 'SpO2 (Oxygen Saturation)', bpm: 'BPM (Heart Rate)', temperature: 'Temperature', hyperemiaIndex: 'Hyperemia Index', volumeIndex: 'Volume Index' }).map(([key, label]) => (
              <label key={key} className="parameter-checkbox">
                <input type="checkbox" checked={selectedParameters[key]} onChange={() => handleParameterToggle(key)} disabled={isTagSubmitting} />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <button type="submit" className="submit-btn" disabled={isTagSubmitting || testsForSelectedTag.length < 2}>
            {isTagSubmitting ? 'Running Analysis...' : 'Run Tag-Based Analysis'}
          </button>
          {tagStatusMessage && (
            <div className={`status-message ${tagStatusMessage.includes('Error') ? 'error' : 'success'}`} style={{ marginTop: '10px' }}>
              {tagStatusMessage}
            </div>
          )}
        </form>
      </div>

      {/* Selected Tests Section - Show when tests are selected (either from clone or Test Results) */}
      {selectedTestIds.length > 0 && (
        <div className="form-section" style={{ marginBottom: '30px', backgroundColor: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
          <h3>Selected Tests ({selectedTestIds.length})</h3>
          <div className="selected-tests-list">
            {selectedTestIds.map((testId, index) => (
              <div key={index} className="test-id-item" style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                <span>{testId}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveTestId(testId)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '3px',
                    padding: '2px 6px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                  title="Remove this test"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {/* CSV Upload and Instructions - Show when there are selected tests */}
          {selectedTestIds.length > 0 && (
            <>
              <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '8px', fontSize: '14px', color: '#333' }}>
                  📄 Upload CSV File with Test IDs
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvUpload}
                  style={{
                    display: 'block',
                    padding: '8px',
                    fontSize: '14px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    width: '100%',
                    cursor: 'pointer'
                  }}
                />
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#6c757d' }}>
                  Format: One test ID per line, or comma-separated values
                </div>
                {csvUploadMessage && (
                  <div style={{
                    marginTop: '10px',
                    padding: '8px 12px',
                    backgroundColor: csvUploadMessage.includes('Error') ? '#f8d7da' : '#d4edda',
                    color: csvUploadMessage.includes('Error') ? '#721c24' : '#155724',
                    borderRadius: '4px',
                    fontSize: '13px',
                    border: `1px solid ${csvUploadMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`
                  }}>
                    {csvUploadMessage}
                  </div>
                )}
              </div>
              <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e7f3ff', borderRadius: '4px', fontSize: '14px', color: '#004085' }}>
                💡 Use the table below to browse and select additional tests, or click the ✕ button to remove tests.
              </div>
            </>
          )}
        </div>
      )}

      {/* Test Selection Table - Show when there are selected tests to allow modification */}
      {selectedTestIds.length > 0 && (
      <div className="test-selection-section">
        <h3 className="section-title">Browse and Select More Tests ({selectedTestIds.length} selected)</h3>

        {/* Pagination Controls - Top */}
        <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>
              Showing {paginatedTests.length > 0 ? (testTablePage - 1) * testTableItemsPerPage + 1 : 0} to{' '}
              {Math.min(testTablePage * testTableItemsPerPage, filteredTests.length)} of {filteredTests.length} results
            </span>
            <select
              value={testTableItemsPerPage}
              onChange={(e) => handleTestTableItemsPerPageChange(Number(e.target.value))}
              style={{
                padding: '4px 8px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
            <button
              onClick={() => handleTestTablePageChange(1)}
              disabled={testTablePage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: testTablePage === 1 ? '#f0f0f0' : 'white',
                color: testTablePage === 1 ? '#999' : '#333',
                cursor: testTablePage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              First
            </button>
            <button
              onClick={() => handleTestTablePageChange(testTablePage - 1)}
              disabled={testTablePage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: testTablePage === 1 ? '#f0f0f0' : 'white',
                color: testTablePage === 1 ? '#999' : '#333',
                cursor: testTablePage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Previous
            </button>

            {/* Page numbers */}
            {(() => {
              const pageNumbers = [];
              const maxPagesToShow = 5;
              let startPage = Math.max(1, testTablePage - Math.floor(maxPagesToShow / 2));
              let endPage = Math.min(testTableTotalPages, startPage + maxPagesToShow - 1);

              if (endPage - startPage + 1 < maxPagesToShow) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
              }

              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(
                  <button
                    key={i}
                    onClick={() => handleTestTablePageChange(i)}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      backgroundColor: testTablePage === i ? '#007bff' : 'white',
                      color: testTablePage === i ? 'white' : 'black',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: testTablePage === i ? 'bold' : 'normal'
                    }}
                  >
                    {i}
                  </button>
                );
              }
              return pageNumbers;
            })()}

            <button
              onClick={() => handleTestTablePageChange(testTablePage + 1)}
              disabled={testTablePage === testTableTotalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: testTablePage === testTableTotalPages ? '#f0f0f0' : 'white',
                color: testTablePage === testTableTotalPages ? '#999' : '#333',
                cursor: testTablePage === testTableTotalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Next
            </button>
            <button
              onClick={() => handleTestTablePageChange(testTableTotalPages)}
              disabled={testTablePage === testTableTotalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: testTablePage === testTableTotalPages ? '#f0f0f0' : 'white',
                color: testTablePage === testTableTotalPages ? '#999' : '#333',
                cursor: testTablePage === testTableTotalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Last
            </button>
          </div>
        </div>

        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="test-selection-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '8px', border: '1px solid #ddd', width: '50px' }}>
                  Select
                </th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span
                      onClick={() => handleTestTableSort('testInstance')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort"
                    >
                      Test ID
                      {testTableSort.key === 'testInstance' && (
                        <span style={{ marginLeft: '5px' }}>
                          {testTableSort.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Filter Test ID"
                    onChange={(e) =>
                      setTestTableFilters((prev) => ({
                        ...prev,
                        testInstance: e.target.value,
                      }))
                    }
                    style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                  />
                </th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span
                      onClick={() => handleTestTableSort('timeStamp')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort"
                    >
                      Timestamp
                      {testTableSort.key === 'timeStamp' && (
                        <span style={{ marginLeft: '5px' }}>
                          {testTableSort.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Filter Timestamp"
                    onChange={(e) =>
                      setTestTableFilters((prev) => ({
                        ...prev,
                        timestamp: e.target.value,
                      }))
                    }
                    style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                  />
                </th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span
                      onClick={() => handleTestTableSort('duration')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort"
                    >
                      Durations
                      {testTableSort.key === 'duration' && (
                        <span style={{ marginLeft: '5px' }}>
                          {testTableSort.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Filter Durations"
                    onChange={(e) =>
                      setTestTableFilters((prev) => ({
                        ...prev,
                        duration: e.target.value,
                      }))
                    }
                    style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                  />
                </th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span
                      onClick={() => handleTestTableSort('tags')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort"
                    >
                      Tags
                      {testTableSort.key === 'tags' && (
                        <span style={{ marginLeft: '5px' }}>
                          {testTableSort.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Filter Tags"
                    onChange={(e) =>
                      setTestTableFilters((prev) => ({
                        ...prev,
                        tags: e.target.value,
                      }))
                    }
                    style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                  />
                </th>
                <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span
                      onClick={() => handleTestTableSort('comment')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort"
                    >
                      Description
                      {testTableSort.key === 'comment' && (
                        <span style={{ marginLeft: '5px' }}>
                          {testTableSort.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </span>
                  </div>
                  <input
                    type="text"
                    placeholder="Filter Description"
                    onChange={(e) =>
                      setTestTableFilters((prev) => ({
                        ...prev,
                        comment: e.target.value,
                      }))
                    }
                    style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedTests.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#6c757d' }}>
                    No tests found matching the filters
                  </td>
                </tr>
              ) : (
                paginatedTests.map((test) => (
                  <tr key={test.testInstance}>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedTestIds.includes(test.testInstance)}
                        onChange={() => handleTestCheckboxToggle(test.testInstance)}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', fontFamily: 'monospace', fontSize: '13px' }}>
                      {test.testInstance}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                      {new Date(test.timeStamp * 1000).toLocaleString()}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '13px', textAlign: 'center' }}>
                      {calculateDuration(test)}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '13px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {(test.tags || []).map((tagId) => {
                          const tag = availableTags.find(t => t._id === tagId);
                          if (!tag) return null;
                          return (
                            <span key={tagId} style={{
                              backgroundColor: tag.color,
                              color: 'white',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '500',
                              whiteSpace: 'nowrap'
                            }}>
                              {tag.name}
                            </span>
                          );
                        })}
                        {(!test.tags || test.tags.length === 0) && '-'}
                      </div>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '13px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={test.comment || ''}>
                      {test.comment || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* Create New Analysis Section */}
      {selectedTestIds.length === 0 ? (
        <div className="no-selection">
          <p>No tests selected for comparison.</p>
          <p>Please select tests from the table above to begin comparative analysis.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="analysis-form">
          {/* Report Name Section */}
          <div className="form-section">
            <h3>Report Name</h3>
            <input
              type="text"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="Enter comparative analysis report name"
              className="report-name-input"
              disabled={isSubmitting}
            />
          </div>

          {/* Parameter Selection Section */}
          <div className="form-section">
            <h3>Analysis Parameters</h3>
            <div className="parameters-grid">
              <label className="parameter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedParameters.spo2}
                  onChange={() => handleParameterToggle('spo2')}
                  disabled={isSubmitting}
                />
                <span>SpO2 (Oxygen Saturation)</span>
              </label>

              <label className="parameter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedParameters.bpm}
                  onChange={() => handleParameterToggle('bpm')}
                  disabled={isSubmitting}
                />
                <span>BPM (Heart Rate)</span>
              </label>

              <label className="parameter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedParameters.temperature}
                  onChange={() => handleParameterToggle('temperature')}
                  disabled={isSubmitting}
                />
                <span>Temperature</span>
              </label>

              <label className="parameter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedParameters.hyperemiaIndex}
                  onChange={() => handleParameterToggle('hyperemiaIndex')}
                  disabled={isSubmitting}
                />
                <span>Hyperemia Index</span>
              </label>

              <label className="parameter-checkbox">
                <input
                  type="checkbox"
                  checked={selectedParameters.volumeIndex}
                  onChange={() => handleParameterToggle('volumeIndex')}
                  disabled={isSubmitting}
                />
                <span>Volume Index</span>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-section">
            <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Running Analysis...' : 'Run Comparative Analysis'}
              </button>
              {isCloneMode && (
                <button
                  type="button"
                  onClick={handleCancelClone}
                  disabled={isSubmitting}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'background-color 0.2s',
                    opacity: isSubmitting ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = '#5a6268')}
                  onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = '#6c757d')}
                >
                  Cancel Clone
                </button>
              )}
            </div>
          </div>

          {/* Status Message */}
          {statusMessage && (
            <div className={`status-message ${statusMessage.includes('Error') ? 'error' : 'success'}`}>
              {statusMessage}
            </div>
          )}
        </form>
      )}

      {/* Existing Reports Table */}
      <div className="reports-section">
        <h3 className="section-title">Comparative Reports ({reports.length})</h3>
        {loadingReports ? (
          <p>Loading reports...</p>
        ) : reports.length === 0 ? (
          <p className="no-reports">No comparative reports generated yet.</p>
        ) : (
          <>
            {/* Pagination Controls - Top */}
            <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  Showing {paginatedReports.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredReports.length)} of {filteredReports.length} results
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: currentPage === 1 ? '#f0f0f0' : 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  First
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: currentPage === 1 ? '#f0f0f0' : 'white',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Previous
                </button>

                {/* Page numbers */}
                {(() => {
                  const pageNumbers = [];
                  const maxPagesToShow = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
                  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

                  if (endPage - startPage + 1 < maxPagesToShow) {
                    startPage = Math.max(1, endPage - maxPagesToShow + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          backgroundColor: currentPage === i ? '#007bff' : 'white',
                          color: currentPage === i ? 'white' : 'black',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: currentPage === i ? 'bold' : 'normal'
                        }}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pageNumbers;
                })()}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: currentPage === totalPages ? '#f0f0f0' : 'white',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Next
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: currentPage === totalPages ? '#f0f0f0' : 'white',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Last
                </button>
              </div>
            </div>

            {selectedReportIds.length > 0 && (
              <div className="bulk-rerun-bar">
                <span>{selectedReportIds.length} report{selectedReportIds.length > 1 ? 's' : ''} selected</span>
                <button className="bulk-rerun-btn" onClick={handleBulkRerun}>🔄 Rerun Selected</button>
              </div>
            )}

            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table className="reports-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={selectedReportIds.length === paginatedReports.length && paginatedReports.length > 0}
                        onChange={handleSelectAllReports}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        title="Select all"
                      />
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('reportId')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Report ID
                          {sortConfig.key === 'reportId' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder="Filter Report ID"
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            reportId: e.target.value,
                          }))
                        }
                        style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                      />
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('reportName')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Report Name
                          {sortConfig.key === 'reportName' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder="Filter Name"
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            reportName: e.target.value,
                          }))
                        }
                        style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                      />
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('createdBy')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Created By
                          {sortConfig.key === 'createdBy' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder="Filter Creator"
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            createdBy: e.target.value,
                          }))
                        }
                        style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                      />
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('status')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Status
                          {sortConfig.key === 'status' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                      <select
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            status: e.target.value,
                          }))
                        }
                        style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                      >
                        <option value="">All</option>
                        <option value="Pending">⏳ Pending</option>
                        <option value="Completed">✅ Completed</option>
                        <option value="Failed">❌ Failed</option>
                      </select>
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('testsCount')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Tests Count
                          {sortConfig.key === 'testsCount' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder="Filter Count"
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            testsCount: e.target.value,
                          }))
                        }
                        style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                      />
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>Tags</th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('parameters')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Parameters
                          {sortConfig.key === 'parameters' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                      <input
                        type="text"
                        placeholder="Filter Parameters"
                        onChange={(e) =>
                          setColumnFilters((prev) => ({
                            ...prev,
                            parameters: e.target.value,
                          }))
                        }
                        style={{ width: '100%', padding: '4px', fontSize: '12px', marginTop: '4px' }}
                      />
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('timestamp')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Created
                          {sortConfig.key === 'timestamp' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span
                          onClick={() => handleSort('lastRerunAt')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          title="Click to sort"
                        >
                          Last Rerun
                          {sortConfig.key === 'lastRerunAt' && (
                            <span style={{ marginLeft: '5px' }}>
                              {sortConfig.direction === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </span>
                      </div>
                    </th>
                    <th style={{ padding: '8px', border: '1px solid #ddd', width: '25%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReports.map((report) => (
                  <tr key={report.reportId}>
                    <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedReportIds.includes(report.reportId)}
                        onChange={() => handleReportCheckboxToggle(report.reportId)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                    <td className="report-id">{report.reportId}</td>
                    <td>{report.reportName}</td>
                    <td className="created-by">{report.createdBy || 'Unknown'}</td>
                    <td>
                      <span className={`status-badge status-${report.status.toLowerCase()}`}>
                        {report.status === 'Pending' && '⏳ '}
                        {report.status === 'Completed' && '✅ '}
                        {report.status === 'Failed' && '❌ '}
                        {report.status}
                      </span>
                    </td>
                    <td className="text-center">
                      {report.testIds?.length || '—'}
                    </td>
                    <td>
                      {report.tags && report.tags.length > 0
                        ? report.tags.map(tagId => {
                            const tag = availableTags.find(t => t._id === tagId);
                            return tag ? (
                              <span key={tagId} style={{ backgroundColor: tag.color, color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '500', marginRight: '4px' }}>
                                {tag.name}
                              </span>
                            ) : tagId;
                          })
                        : '—'}
                    </td>
                    <td className="parameters">{report.parameters.join(', ')}</td>
                    <td>{formatTimestamp(report.timestamp)}</td>
                    <td>{formatLastRerun(report)}</td>
                    <td style={{ width: '25%' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
                        {report.status === 'Completed' && (
                          <button className="view-report-btn" onClick={() => window.open(`${API_BASE_URL}/comparative-analysis/report/${report.reportId}?token=${authService.getToken()}`, '_blank')}>
                            📄 View Report
                          </button>
                        )}
                        <button
                          className="clone-report-btn"
                          onClick={() => handleCloneReport(report)}
                          title="Clone this report"
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '6px 12px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = '#138496'}
                          onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
                        >
                          📋 Clone
                        </button>
                        <button
                          className="rerun-report-btn"
                          onClick={() => handleRerunReport(report.reportId)}
                          title="Regenerate HTML report"
                        >
                          🔄 Rerun
                        </button>
                        <button
                          className="delete-report-btn"
                          onClick={() => handleDeleteReport(report.reportId)}
                          title="Delete report"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px' }}>
                        <span title="Schedule daily auto-rerun (UTC time)" style={{ fontSize: '12px', color: '#555' }}>⏰ UTC</span>
                        <input
                          type="time"
                          value={scheduleMap[report.reportId]?.input ?? (report.scheduleTime || '')}
                          onChange={(e) => handleScheduleChange(report.reportId, e.target.value)}
                          style={{ fontSize: '12px', padding: '2px 4px', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <button
                          onClick={() => handleScheduleSave(report.reportId)}
                          disabled={scheduleMap[report.reportId]?.saving}
                          title="Save schedule"
                          style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          {scheduleMap[report.reportId]?.saving ? '...' : '🔄 Sched'}
                        </button>
                        {report.scheduleTime && (
                          <button
                            onClick={() => handleScheduleClear(report.reportId)}
                            title="Clear schedule"
                            style={{ fontSize: '11px', padding: '2px 6px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                          >
                            ✕ Clear
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {rerunModalReportIds && (
        <div className="rerun-modal-overlay">
          <div className="rerun-modal">
            <h3>
              {rerunModalReportIds.length === 1
                ? `Rerun: ${reports.find(r => r.reportId === rerunModalReportIds[0])?.reportName || rerunModalReportIds[0]}`
                : `Rerun ${rerunModalReportIds.length} reports`}
            </h3>
            <p className="rerun-modal-subtitle">Choose how individual test reports should be regenerated before the comparative analysis reruns.</p>
            <div className="rerun-mode-options">
              <label className="rerun-mode-btn incremental">
                <input
                  type="radio"
                  name="cleanRerunFlag"
                  value="n"
                  checked={cleanRerunFlag === 'n'}
                  onChange={() => setCleanRerunFlag('n')}
                />
                <span>⚡ Incremental Rerun</span>
                <span className="rerun-mode-desc">Only regenerates tests that haven't been analyzed yet. Faster — recommended for routine reruns.</span>
              </label>
              <label className="rerun-mode-btn clean">
                <input
                  type="radio"
                  name="cleanRerunFlag"
                  value="y"
                  checked={cleanRerunFlag === 'y'}
                  onChange={() => setCleanRerunFlag('y')}
                />
                <span>🔁 Clean Rerun</span>
                <span className="rerun-mode-desc">Regenerates every individual report from scratch. Use after code or metric changes.</span>
              </label>
            </div>
            <div className="rerun-modal-actions">
              <button className="rerun-modal-cancel-btn" onClick={() => setRerunModalReportIds(null)}>Cancel</button>
              <button className="rerun-modal-confirm-btn" onClick={handleConfirmRerun}>🔄 Rerun</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ComparativeAnalysis;
